
import React from "react";

// ========================================
// PRODUCT MEDIA
// ========================================

const ProductMedia = ({
    data = [],
    onChange
}) => {

    const media = Array.isArray(data)
        ? data
        : [];

    // ========================================
    // STYLES
    // ========================================

    const inputClass =
        "w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-300";

    const labelClass =
        "block text-sm font-medium text-gray-700 mb-2";

    const addButtonClass =
        "px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700";

    const removeButtonClass =
        "px-3 py-2 bg-red-50 text-red-700 border border-red-200 text-sm rounded hover:bg-red-100";

    // ========================================
    // ADD MEDIA
    // ========================================

    const addMedia = () => {

        const nextOrder = media.reduce(
            (max, item) =>
                Math.max(
                    max,
                    Number(item.displayOrder) || 0
                ),
            0
        ) + 1;

        onChange([
            ...media,
            {
                type: "image",
                title: "",
                description: "",
                category: "",
                url: "",
                approvedForPublicUse: false,
                displayOrder: nextOrder
            }
        ]);

    };

    // ========================================
    // UPDATE MEDIA
    // ========================================

    const updateMedia = (
        index,
        field,
        value
    ) => {

        onChange(
            media.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );

    };

    // ========================================
    // REMOVE MEDIA
    // ========================================

    const removeMedia = (index) => {

        const updatedMedia = media.filter(
            (_, i) => i !== index
        );

        onChange(
            updatedMedia.map((item, i) => ({
                ...item,
                displayOrder: i + 1
            }))
        );

    };

    // ========================================
    // MOVE MEDIA
    // ========================================

    const moveMedia = (index, direction) => {

        const targetIndex = index + direction;

        if (
            targetIndex < 0 ||
            targetIndex >= media.length
        ) {
            return;
        }

        const updatedMedia = [...media];

        [
            updatedMedia[index],
            updatedMedia[targetIndex]
        ] = [
            updatedMedia[targetIndex],
            updatedMedia[index]
        ];

        onChange(
            updatedMedia.map((item, i) => ({
                ...item,
                displayOrder: i + 1
            }))
        );

    };

    // ========================================
    // MEDIA PREVIEW
    // ========================================

    const renderPreview = (item) => {

        const url = (item.url || "").trim();

        if (!/^https?:\/\//i.test(url)) {
            return null;
        }

        if (item.type === "image") {

            return (

                <div className="mt-3 border rounded-lg p-3 bg-white">

                    <img
                        src={url}
                        alt={item.title || "Product media"}
                        className="max-h-64 max-w-full object-contain rounded"
                        loading="lazy"
                    />

                </div>

            );

        }

        return (

            <div className="mt-3">

                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-700 hover:underline break-all"
                >
                    Open {item.type || "media"} ↗
                </a>

            </div>

        );

    };

    // ========================================
    // RENDER
    // ========================================

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 border-b pb-4">

                <div>

                    <h3 className="text-base font-semibold text-gray-900">
                        Product Media
                    </h3>

                    <p className="text-sm text-gray-500 mt-1">
                        Manage product images, videos, PDFs
                        and supporting documents.
                    </p>

                </div>

                <button
                    type="button"
                    onClick={addMedia}
                    className={addButtonClass}
                >
                    + Add Media
                </button>

            </div>

            {/* EMPTY STATE */}

            {media.length === 0 && (

                <div className="py-12 text-center border border-dashed rounded-lg">

                    <p className="text-sm text-gray-500">
                        No product media added yet.
                    </p>

                    <p className="text-xs text-gray-400 mt-2">
                        Add images, videos, technical datasheets
                        or product documents.
                    </p>

                </div>

            )}

            {/* MEDIA LIST */}

            {media.map((item, index) => (

                <div
                    key={item._id || index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                >

                    {/* MEDIA HEADER */}

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-100 px-5 py-4">

                        <div>

                            <h4 className="font-semibold text-gray-900">
                                Media {index + 1}
                            </h4>

                            {item.title && (

                                <p className="text-xs text-gray-500 mt-1">
                                    {item.title}
                                </p>

                            )}

                        </div>

                        <div className="flex flex-wrap gap-2">

                            <button
                                type="button"
                                onClick={() =>
                                    moveMedia(index, -1)
                                }
                                disabled={index === 0}
                                className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                            >
                                ↑ Up
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    moveMedia(index, 1)
                                }
                                disabled={index === media.length - 1}
                                className="px-3 py-2 border rounded text-sm bg-white disabled:opacity-40"
                            >
                                ↓ Down
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    removeMedia(index)
                                }
                                className={removeButtonClass}
                            >
                                Remove
                            </button>

                        </div>

                    </div>

                    {/* MEDIA FIELDS */}

                    <div className="p-5 space-y-5">

                        {/* TYPE AND CATEGORY */}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                            <div>

                                <label className={labelClass}>
                                    Media Type *
                                </label>

                                <select
                                    value={item.type || "image"}
                                    onChange={(event) =>
                                        updateMedia(
                                            index,
                                            "type",
                                            event.target.value
                                        )
                                    }
                                    className={inputClass}
                                >
                                    <option value="image">
                                        Image
                                    </option>

                                    <option value="video">
                                        Video
                                    </option>

                                    <option value="pdf">
                                        PDF
                                    </option>

                                    <option value="document">
                                        Document
                                    </option>
                                </select>

                            </div>

                            <div>

                                <label className={labelClass}>
                                    Category
                                </label>

                                <input
                                    type="text"
                                    value={item.category || ""}
                                    onChange={(event) =>
                                        updateMedia(
                                            index,
                                            "category",
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. Product Image, Technical Datasheet"
                                    className={inputClass}
                                />

                            </div>

                        </div>

                        {/* TITLE */}

                        <div>

                            <label className={labelClass}>
                                Media Title *
                            </label>

                            <input
                                type="text"
                                value={item.title || ""}
                                onChange={(event) =>
                                    updateMedia(
                                        index,
                                        "title",
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Product Technical Datasheet"
                                className={inputClass}
                            />

                        </div>

                        {/* DESCRIPTION */}

                        <div>

                            <label className={labelClass}>
                                Description
                            </label>

                            <textarea
                                value={item.description || ""}
                                onChange={(event) =>
                                    updateMedia(
                                        index,
                                        "description",
                                        event.target.value
                                    )
                                }
                                placeholder="Describe the media and its relevance to the product."
                                rows={3}
                                className={inputClass}
                            />

                        </div>

                        {/* URL */}

                        <div>

                            <label className={labelClass}>
                                Media URL *
                            </label>

                            <input
                                type="url"
                                value={item.url || ""}
                                onChange={(event) =>
                                    updateMedia(
                                        index,
                                        "url",
                                        event.target.value
                                    )
                                }
                                placeholder="https://example.com/product-datasheet.pdf"
                                className={inputClass}
                            />

                            <p className="text-xs text-gray-500 mt-2">
                                Enter a direct HTTPS URL to the
                                media file or a supported external
                                media page.
                            </p>

                            {renderPreview(item)}

                        </div>

                        {/* DISPLAY ORDER */}

                        <div className="max-w-xs">

                            <label className={labelClass}>
                                Display Order
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={item.displayOrder ?? 0}
                                onChange={(event) =>
                                    updateMedia(
                                        index,
                                        "displayOrder",
                                        event.target.value === ""
                                            ? 0
                                            : Number(event.target.value)
                                    )
                                }
                                className={inputClass}
                            />

                        </div>

                        {/* PUBLIC APPROVAL */}

                        <div className="border-t pt-5">

                            <div className="border rounded-lg p-4 bg-gray-50">

                                <label className="flex items-start gap-3 cursor-pointer">

                                    <input
                                        type="checkbox"
                                        checked={
                                            item.approvedForPublicUse === true
                                        }
                                        onChange={(event) =>
                                            updateMedia(
                                                index,
                                                "approvedForPublicUse",
                                                event.target.checked
                                            )
                                        }
                                        className="mt-1 h-4 w-4"
                                    />

                                    <div>

                                        <span className="block text-sm font-medium text-gray-900">
                                            Approved for Public Use
                                        </span>

                                        <p className="text-xs text-gray-500 mt-1">
                                            Allow this media to be used
                                            in customer-facing AI
                                            responses and product
                                            presentations once the
                                            overall product is approved.
                                        </p>

                                    </div>

                                </label>

                            </div>

                        </div>

                    </div>

                </div>

            ))}

            {/* GUIDANCE */}

            <div className="border-t pt-4">

                <p className="text-xs text-gray-500">
                    Product media should be accurate, relevant
                    and appropriately licensed. Only media
                    explicitly approved for public use should
                    be exposed through customer-facing AI tools.
                    Adding a PDF URL does not automatically
                    extract or index its contents for retrieval.
                </p>

            </div>

        </div>

    );

};

export default ProductMedia;
